import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Plus, Link as LinkIcon, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { Pagination, Modal, Button, Form, Select, Input, message, Typography, Space, Divider } from 'antd';
import { fetchUserData } from '@/api/Admin/User';
import { fetchPackageAdminList } from '@/api/Admin/userPackage';
import { getUserBankAccount } from '@/api/Admin/Bank';
import { generateSigningContractUrl, SigningContractPayload } from '@/lib/signingContractPayload';
import { CRM_HOST } from '@/env';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Contract {
  contract_info: any | null;
  contract_file_url: string | null;
  created_at: string;
}

interface DocumentsTabProps {
  userId: number;
  docs?: Contract[];
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ 
  userId,
  docs,
  currentPage = 1,
  totalItems = 0,
  onPageChange
}) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [bankInfo, setBankInfo] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [packages, setPackages] = useState<any[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (userId) {
      fetchUserData(userId.toString()).then((data) => setUser(data));
      getUserBankAccount(userId.toString())
        .then((res) => {
          if (res.status && res.data) {
            setBankInfo(res.data);
          }
        })
        .catch((err) => console.error('Failed to fetch bank info:', err));
    }
  }, [userId]);

  const loadPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await fetchPackageAdminList();
      if (response.status && response.data) {
        setPackages(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      message.error(t('common.errorFetchingPackages', 'Lỗi khi lấy danh sách gói'));
    } finally {
      setLoadingPackages(false);
    }
    return [];
  };

  const handleOpenModal = async (existingContract?: Contract) => {
    setIsModalVisible(true);
    setGeneratedUrl(null);
    form.resetFields();
    
    let currentPackages = packages;
    if (packages.length === 0) {
      currentPackages = await loadPackages();
    }

    if (existingContract) {
      const contractCode = existingContract.contract_info?.contract_code || '';
      // Try to find package from contract code
      let foundPackageId = undefined;
      if (contractCode && currentPackages.length > 0) {
        const pkg = currentPackages.find((p: any) => contractCode.toUpperCase().includes(p.name.toUpperCase()));
        if (pkg) foundPackageId = pkg.id;
      }

      form.setFieldsValue({
        contractNumber: contractCode,
        packageId: foundPackageId,
        bank: bankInfo?.bank_name || '',
        bankAccount: bankInfo?.account_number || '',
      });
    } else {
      form.setFieldsValue({
        bank: bankInfo?.bank_name || '',
        bankAccount: bankInfo?.account_number || '',
      });
    }
  };

  const onFinish = async (values: any) => {
    if (!user) {
      message.error(t('investorDetail.documents.userNotFound', 'Không tìm thấy thông tin người dùng'));
      return;
    }

    setGenerating(true);
    try {
      const selectedPackage = packages.find(p => p.id === values.packageId);
      
      const payload: SigningContractPayload = {
        packageId: values.packageId,
        contractInfo: {
          name: selectedPackage?.name,
          price: Number(selectedPackage?.price),
        },
        contractData: {
          contractNumber: values.contractNumber,
          signDate: dayjs().format('YYYY-MM-DD'),
          depositDate: dayjs().format('YYYY-MM-DD'),
          finalPaymentDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
          investor: {
            fullName: user.fullname || `${user.first_name} ${user.last_name}`,
            idNumber: user.cccd_number,
            dob: user.birthday,
            issueDate: user.cccd_issued_date,
            issuePlace: user.cccd_issued_place,
            email: user.email,
            phone: user.phone,
            address: user.address || `${user.city || ''}, ${user.country || ''}`,
            bankAccount: values.bankAccount || '',
            bank: values.bank || '',
          },
        },
      };

      const url = await generateSigningContractUrl(payload);
      const fullUrl = `${CRM_HOST}${url}`;
      setGeneratedUrl(fullUrl);
      message.success(t('investorDetail.documents.generateSuccess', 'Đã tạo URL hợp đồng thành công!'));
    } catch (error) {
      console.error('Failed to generate URL:', error);
      message.error(t('investorDetail.documents.generateError', 'Lỗi khi tạo URL hợp đồng'));
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      message.success(t('common.copied', 'Đã sao chép!'));
    }
  };

  const contracts: Contract[] = docs || [];

  const handleDownload = (contract: Contract) => {
    window.open(contract.contract_file_url || '', '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Title & Action */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('investorDetail.documents.title', 'Danh sách hợp đồng')}
        </h3>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('investorDetail.documents.generateBtn', 'Tạo hợp đồng mới')}
        </button>
      </div>

      {/* Contracts List */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-125">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.documents.code', 'Mã HĐ')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.documents.name', 'Tên')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.documents.signDate', 'Ngày ký')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.documents.download', 'Tải xuống')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.documents.recreate', 'Tạo lại')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {contracts.map((contract) => (
              <tr key={contract.contract_info?.contract_code || contract.created_at} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap" title={contract.contract_info?.contract_code}>
                  {contract.contract_info?.contract_code.length > 20 ? contract.contract_info?.contract_code.slice(0, 20) + '...' : contract.contract_info?.contract_code}
                  {!contract.contract_info?.contract_code && 'N/A'}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    {contract.contract_info?.file_name || 'N/A'}
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {contract.created_at}
                </td>
                <td className="px-3 py-3 text-center">
                  <button
                    disabled={!contract.contract_file_url}
                    onClick={() => handleDownload(contract)}
                    className="inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed! gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </td>
                <td className="px-3 py-3 text-center">
                  <button
                    onClick={() => handleOpenModal(contract)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md transition-colors"
                    title={t('investorDetail.documents.recreateAlt', 'Tạo lại hợp đồng với thông tin này')}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && onPageChange && (
        <div className="flex justify-end pt-4">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* Generate Contract Modal */}
      <Modal
        title={t('investorDetail.documents.generateModalTitle', 'Tạo hợp đồng ký kết mới')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            signDate: dayjs(),
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="packageId"
              label={t('investorDetail.documents.selectPackage', 'Chọn gói đầu tư')}
              rules={[{ required: true, message: t('common.required', 'Trường này là bắt buộc') }]}
            >
              <Select
                placeholder={t('investorDetail.documents.packagePlaceholder', 'Chọn một gói')}
                loading={loadingPackages}
              >
                {packages.map((pkg) => (
                  <Option key={pkg.id} value={pkg.id}>
                    {pkg.name} - {Number(pkg.price).toLocaleString()} VND
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="contractNumber"
              label={t('investorDetail.documents.contractNumber', 'Số hợp đồng')}
            >
              <Input placeholder="BEQ-2026-XXXX" />
            </Form.Item>

            <Form.Item
              name="bank"
              label={t('investorDetail.documents.bank', 'Ngân hàng')}
            >
              <Input placeholder="Vietcombank" />
            </Form.Item>

            <Form.Item
              name="bankAccount"
              label={t('investorDetail.documents.bankAccount', 'Số tài khoản')}
            >
              <Input placeholder="0123456789" />
            </Form.Item>
          </div>

          <Divider />

          {generatedUrl ? (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Text strong>{t('investorDetail.documents.generatedLink', 'Link hợp đồng đã tạo')}</Text>
                <Button 
                  type="link" 
                  icon={<Copy className="w-4 h-4" />} 
                  onClick={copyToClipboard}
                >
                  {t('common.copy', 'Sao chép')}
                </Button>
              </div>
              <div className="p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded break-all">
                <Text copyable={{ text: generatedUrl }}>{generatedUrl}</Text>
              </div>
              <div className="flex justify-center pt-2">
                <Button 
                  type="primary" 
                  icon={<ExternalLink className="w-4 h-4" />}
                  href={generatedUrl}
                  target="_blank"
                >
                  {t('investorDetail.documents.openLink', 'Mở link ký kết')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-3">
              <Button onClick={() => setIsModalVisible(false)}>
                {t('common.cancel', 'Hủy')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={generating}
              >
                {t('investorDetail.documents.generateSubmit', 'Tạo Link Hợp Đồng')}
              </Button>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DocumentsTab;
